import { usePostAuthorizationAPI } from '@api/auth';
import { projectAtom } from '@data/auth';
import { useNotification } from '@data/notification';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrevious } from 'react-use';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  VehicleTelemetry,
  WebSocketVehicleMetadata,
} from '@data/fms/vehicle/types';
import type { Schedule } from '@data/fms/schedule/types';
import { overrideTelemetryEmergencyStatus } from '@data/fms/vehicle/utils';
import { getExponentialBackoffDelay } from '@data/common/webSocket/utils';
import { MAX_RETRY_COUNT } from '@data/common/webSocket/constants';
import { useFMSWebSocketHeartBeat } from './heartbeat';
import { isWebsocketErrorAtom } from '@data/common/webSocket/states';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';

type channelType =
  | 'environmentVehiclesTelemetry'
  | 'environmentVehiclesMetadataUpdates'
  | 'vehicleActiveSchedule';

type Props<T extends channelType> = {
  channel: T;
};

type ResultType<T extends channelType> =
  T extends 'environmentVehiclesTelemetry'
    ? VehicleTelemetry
    : T extends 'environmentVehiclesMetadataUpdates'
    ? WebSocketVehicleMetadata
    : T extends 'vehicleActiveSchedule'
    ? Schedule
    : never;

export const useFMSWebSocket = <T extends channelType>({
  channel,
}: Props<T>) => {
  const [data, setData] = useState<ResultType<T> | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const prevSocket = usePrevious(socket);
  const retryCount = useRef<number>(0);
  const isError = useRef<boolean>(false);
  const resourceId = useRef<string | null>(null);
  const accessToken = useRef<string | null>(null);
  const isUserClose = useRef(false);
  const isVehicleChange = useRef(false);
  const getToken = usePostAuthorizationAPI();
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const { isConnecting, checkConnection, checkReceivedData, closeHeartBeat } =
    useFMSWebSocketHeartBeat();
  const prevIsConnecting = usePrevious(isConnecting);
  const setIsWebsocketError = useSetAtom(isWebsocketErrorAtom);

  // VehiclesTelemetry の場合
  const channelIsEnvironmentVehiclesTelemetry =
    channel === 'environmentVehiclesTelemetry';
  // VehiclesMetadata の場合
  const channelIsEnvironmentVehiclesMetadataUpdates =
    channel === 'environmentVehiclesMetadataUpdates';
  // vehicleActiveSchedule の場合
  const channelIsVehicleActiveSchedule = channel === 'vehicleActiveSchedule';
  const createSocket = useAtomCallback(
    useCallback(
      async (get, _, vehicleId?: string | null) => {
        isUserClose.current = false;
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        const token = await getToken();
        const targetResourceId = channelIsVehicleActiveSchedule
          ? vehicleId
          : environment?.environment_id;
        if (!project || !targetResourceId || !token.accessToken) {
          notifyError({
            message: t('api.fms.connect_websocket', { status: 'failed' }),
          });
          return;
        }

        resourceId.current = targetResourceId;
        accessToken.current = token.accessToken;

        const s = new WebSocket(
          `wss://${import.meta.env.VITE_FMS_API_DOMAIN}/${
            import.meta.env.VITE_FMS_API_VERSION
          }/ws?project_id=${project.id}&token=${token.accessToken}`,
        );

        if (!s) {
          notifyError({
            message: t('api.fms.connect_websocket', { status: 'failed' }),
          });
          return;
        } else {
          setSocket(s);
        }
      },
      [channelIsVehicleActiveSchedule, getToken, notifyError, t],
    ),
  );

  const closeSocket = useCallback(
    (doVehicleChange?: boolean) => {
      isUserClose.current = true;
      isVehicleChange.current = !!doVehicleChange;
      socket?.close();
      closeHeartBeat();
    },
    [socket, closeHeartBeat],
  );

  const onOpen = useCallback(() => {
    // 接続成功時にリトライカウントをリセット
    retryCount.current = 0;
    // 車両切り替えフラグをリセット
    isVehicleChange.current = false;
    // エラーフラグをリセット
    isError.current = false;
    if (!socket || !resourceId.current || !accessToken.current) {
      return;
    }
    console.group('FMS Websocket API Open');
    console.log(
      `%ctime: %c${DateTime.now().toFormat('HH:mm:ss')}`,
      'color: #2196f3;',
      'color: #66bb6a',
    );
    console.log(
      `%cchannel: %c${channel}`,
      'color: #2196f3;',
      'color: #fdd835 ',
    );
    console.log('%cresourceId:', 'color: #2196f3;', resourceId.current);
    console.groupEnd();
    socket.send(
      JSON.stringify({
        token: accessToken.current,
        operation: 'subscribe',
        channel,
        resource_id: resourceId.current,
      }),
    );
    checkConnection(socket);
  }, [channel, socket, checkConnection]);

  const onMessage = useCallback(
    (e: MessageEvent) => {
      checkReceivedData(e);
      if (e.data === 'pong') return;
      try {
        const updatedData = JSON.parse(e.data);
        // VehiclesTelemetry の場合
        if (channelIsEnvironmentVehiclesTelemetry) {
          if (updatedData?.status === 'shutdown')
            overrideTelemetryEmergencyStatus(updatedData);
          setData(updatedData);
          return;
        }
        // VehiclesMetadata の場合
        if (channelIsEnvironmentVehiclesMetadataUpdates)
          return setData(updatedData);
        // vehicleActiveSchedule の場合
        if (channelIsVehicleActiveSchedule) return setData(updatedData);
      } catch (err) {
        console.log(err);
      }
    },
    [
      channelIsEnvironmentVehiclesMetadataUpdates,
      channelIsEnvironmentVehiclesTelemetry,
      channelIsVehicleActiveSchedule,
      checkReceivedData,
    ],
  );

  const retryConnection = useCallback(() => {
    // 車両切り替え時かつ、エラーが無い場合はリトライして処理を終了
    if (isVehicleChange.current && !isError.current)
      return createSocket(resourceId?.current);
    // リトライ回数の最大値を超えた場合はログを出力して処理を終了
    if (retryCount.current >= MAX_RETRY_COUNT) {
      console.error(
        'WebSocket connection retry limit exceeded\nchannel:',
        channel,
      );
      setIsWebsocketError(true);
      return;
    }

    const retryDelay = getExponentialBackoffDelay(retryCount.current);
    // 時間を開けてリトライ
    setTimeout(() => {
      retryCount.current = retryCount.current + 1;
      createSocket(channelIsVehicleActiveSchedule ? resourceId?.current : null);
    }, retryDelay);
  }, [
    channel,
    channelIsVehicleActiveSchedule,
    createSocket,
    setIsWebsocketError,
  ]);

  const onError = useCallback(() => {
    isError.current = true;
  }, []);

  const onClose = useCallback(() => {
    if (!socket) return;
    console.group('FMS Websocket API Close');
    console.log(
      `%ctime: %c${DateTime.now().toFormat('HH:mm:ss')}`,
      'color: #2196f3;',
      'color: #66bb6a',
    );
    console.log(`%cchannel: %c${channel}`, 'color: #2196f3;', 'color: #f44336');
    console.log('%cresourceId:', 'color: #2196f3;', resourceId.current);
    console.groupEnd();
    socket.removeEventListener('open', onOpen);
    socket.removeEventListener('close', onClose);
    socket.removeEventListener('message', onMessage);
    socket.removeEventListener('error', onError);
    setSocket(null);
    if (!isUserClose.current) {
      retryConnection();
    }
  }, [socket, channel, onOpen, onMessage, onError, retryConnection]);

  useEffect(() => {
    if (!prevSocket && socket) {
      socket.addEventListener('open', onOpen);
      socket.addEventListener('close', onClose);
      socket.addEventListener('message', onMessage);
      socket.addEventListener('error', onError);
    }
  }, [socket, prevSocket, onOpen, onMessage, onClose, onError]);

  useEffect(() => {
    // pong が指定秒数以内に受信できなかった場合
    if (prevIsConnecting && !isConnecting) {
      // フロント側で close し、 close イベントを trigger して再接続する
      socket?.removeEventListener('open', onOpen);
      socket?.removeEventListener('close', onClose);
      socket?.removeEventListener('message', onMessage);
      socket?.removeEventListener('error', onError);
      socket?.close();
      setSocket(null);
      if (!isUserClose.current) {
        retryConnection();
      }
    }
  }, [
    socket,
    isConnecting,
    prevIsConnecting,
    onOpen,
    onClose,
    onMessage,
    onError,
    retryConnection,
  ]);

  return {
    createSocket,
    closeSocket,
    data,
  };
};
